import React, { Fragment, useEffect, useState } from "react"
import { gql, useQuery } from "@apollo/client"

import { FieldArray, ErrorMessage } from "formik"
import { Fieldset } from "components"

import formatDate from "utils/formatDate"
import { stringifyNumber } from "utils"
import uniqBy from "lodash/uniqBy"

const TripGeneral = ({ values }) => {
  return (
    <>
      <TripGeneralForm {...values} />
      <TripLegalForm {...values} />
      <TripOrganisationForm {...values} />
    </>
  )
}

export default TripGeneral

//TODO: Refactor
//FORMS

const TODAY = new Date().toISOString()
const TripGeneralForm = ({ type, startDate, endDate, payment }) => {
  const deposits = payment?.deposits
  const startDateMin = formatDate(TODAY, "yyyy-LL-dd")
  const endDateMin = formatDate(startDate || TODAY, "yyyy-LL-dd")

  const [countries, setCountries] = useState(null)
  const [options, setOptions] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadCountries = async () => {
    try {
      const countries = await fetch("/countries.json").then((_) => _.json())
      setCountries(countries)
      setOptions(countries)
      setIsLoading(false)
    } catch (error) {}
  }

  useEffect(() => {
    if (!countries) {
      loadCountries()
    }
  }, [countries])

  const handleSearch = (search, e) => {
    setIsLoading(true)
    if (!search) {
      setOptions(countries)
      setIsLoading(false)
      return
    }

    if (e.action === "input-change") {
      // links[j].text.indexOf(queryTerms[i])
      // countries.contains(search)
      setOptions(null)

      const found = countries
        .filter((_) => _.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
        .map((_) => ({
          ..._,
          score: _.name.toLowerCase().indexOf(search.toLowerCase()),
        }))
        .sort((a, b) => a.score - b.score)
      setOptions(found)
    }

    setIsLoading(false)
  }

  const _options = options?.map((_) => ({
    label: _.name,
    value: { ..._ },
  }))

  return (
    <>
      <div className="px-2 py-3">
        <Fieldset
          aspect="16:9"
          library={true}
          component="image"
          name="image"
          label="Cover image *"
          preload={true}
        />
      </div>

      <div className="py-3 flex justify-between">
        <div className="px-2 w-8/12">
          <Fieldset
            name="name"
            label="Name *"
            type="text"
            placeholder="Enter trip name"
            maxlength={35}
          />
        </div>

        <div className="px-2 w-4/12">
          <Fieldset
            name="code"
            label="ID *"
            type="number"
            placeholder="Enter trip ID"
          />
        </div>
      </div>

      <div className="py-3 flex justify-between">
        <div className="px-2 flex-1">
          <Fieldset
            name="startDate"
            label="Start date *"
            type="date"
            min={startDateMin}
          />
        </div>

        <div className="px-2 flex-1">
          <Fieldset
            name="endDate"
            label="End date *"
            type="date"
            min={endDateMin}
          />
        </div>
      </div>

      <div className="px-2 py-3">
        {/* <Fieldset
          name="location"
          label="Destinations (Country) *"
          type="text"
          placeholder="Enter trip destinations"
        /> */}

        <Fieldset
          name="destinations"
          label="Destinations (Countries) *"
          placeholder="Enter trip destinations"
          component="select"
          options={_options}
          cacheOptions
          isMulti
          isLoading={isLoading}
          onInputChange={handleSearch}
          getOptionValue={(opt) => opt.label}
          mapping={{ label: "name" }}
        />
      </div>

      <div className="px-2 py-3 mt-4 w-6/12">
        <Fieldset
          name="payment.price"
          label="Base price"
          type="price"
          placeholder="Enter trip base price in USD"
        />
      </div>

      {/* {type === "EXTERNAL" && ( */}
      <div className="px-2 py-3">
        <FieldArray
          name="payment.deposits"
          render={(arrayHelpers) => (
            <>
              <label className="block text-sm font-medium text-gray-700">
                Payment schedule:
              </label>
              <header className="mt-2 flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                <div className="w-3/12">
                  <p>Deposits</p>
                </div>

                <div className="w-3/12 ">
                  <p>Amount</p>
                </div>

                <div className="w-4/12">
                  <p>Due date</p>
                </div>

                <div className="w-1/12"></div>
              </header>

              <FieldDeposit
                data={deposits}
                endDateMax={endDate}
                arrayHelpers={arrayHelpers}
              />
            </>
          )}
        />

        <ErrorMessage name="payment.deposits">
          {(message) =>
            typeof message === "string" && (
              <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
                {message}
              </div>
            )
          }
        </ErrorMessage>
      </div>
      {/* )} */}
    </>
  )
}

const CreateButton = ({ title, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-blue-500 hover:text-blue-700 py-2 px-1 my-6 rounded flex"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>

      <span className="ml-2">{title}</span>
    </button>
  )
}

const DeleteButton = ({ onClick }) => {
  return (
    <button type="button" className="inline-block" onClick={onClick}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
          fill="#4A5568"
        />
      </svg>
    </button>
  )
}

const FieldDeposit = ({ data, endDateMax, arrayHelpers }) => {
  const lastIndex = data?.length - 1
  const depositModel = {
    amount: "",
    dueDate: "",
  }

  return (
    <div className="flex flex-col">
      {data?.map((part, index) => (
        <Fragment key={index}>
          {index === data.length - 1 && (
            <CreateButton
              title="Add deposit"
              onClick={() => arrayHelpers.insert(lastIndex, depositModel)}
            />
          )}

          <div className="flex justify-between items-center my-4 px-6 space-x-2">
            <div className="w-3/12 text-base capitalize">
              <label>
                {data.length === 1 || index !== data.length - 1
                  ? `${stringifyNumber(index + 1)} deposit`
                  : "Final payment"}
              </label>
            </div>

            <div className="w-3/12">
              {index !== data?.length - 1 && (
                <Fieldset
                  name={`payment.deposits[${index}].amount`}
                  type="price"
                />
              )}
            </div>

            <div className="w-4/12 flex justify-between items-center">
              {index === 0 ? (
                <p className="text-gray-700 text-base">At Registration</p>
              ) : (
                <Fieldset
                  name={`payment.deposits[${index}].dueDate`}
                  type="date"
                  min={TODAY}
                  max={endDateMax}
                />
              )}
            </div>

            <div className="w-1/12 flex justify-end items-center">
              {![0, data.length - 1].includes(index) && (
                <DeleteButton onClick={() => arrayHelpers.remove(index)} />
              )}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  )
}

const TripLegalForm = ({ type }) => {
  const CONTRACTS = gql`
    query Contracts($page: Int, $size: Int) {
      contracts(page: $page, size: $size) {
        id
        name
      }
    }
  `

  const { data, loading } = useQuery(CONTRACTS, {
    variables: { page: 0, size: 100 },
  })

  const contracts = data?.contracts?.map((contract) => ({
    label: contract.name,
    value: { ...contract },
  }))

  return (
    <div className="w-full mt-16">
      <div className="px-2 py-3 mt-2">
        <Fieldset
          component="select"
          // label="Contract *"
          label="Terms and Conditions *"
          name="legal.contract"
          isLoading={loading}
          mapping={{ label: "name" }}
          options={contracts}
          placeholder="Select trip contract"
        />
      </div>
    </div>
  )
}

const FieldAgency = () => {
  const AGENCIES = gql`
    query Agencies(
      $filter: AgencyFilterInput
      $page: Int
      $size: Int
      $sort: AgencySortInput
    ) {
      agencies(filter: $filter, page: $page, size: $size, sort: $sort) {
        id
        name
        description
        logoURL
      }
    }
  `

  const variables = {
    page: 0,
    size: 10,
    // filter: { roles: ["ADMIN", "AGENT", "LEADER"] },
    // sort: { displayName: "ASC" },
  }

  const { data, loading, fetchMore } = useQuery(AGENCIES, {
    variables,
    notifyOnNetworkStatusChange: true,
  })

  const agencies = data?.agencies?.map((agency) => ({
    key: agency.id,
    label: agency.name,
    value: { ...agency },
  }))

  const handleSearch = (_search, e) => {
    if (e.action === "input-change") {
      const search = _search?.toLowerCase()
      fetchMore({
        variables: { ...variables, filter: { ...variables?.filter, search } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            agencies: uniqBy(
              [...prev.agencies, ...fetchMoreResult.agencies],
              "name",
            ),
          })
        },
      })
    }
  }

  return (
    <Fieldset
      name="agency"
      label="Agency *"
      component="select"
      options={agencies}
      onInputChange={handleSearch}
      getOptionValue={(opt) => opt.key}
      mapping={{ label: "name" }}
      placeholder="Select trip agency"
    />
  )
}

const TripOrganisationForm = () => {
  const USERS = gql`
    query Users(
      $filter: UserFilterInput
      $page: Int
      $size: Int
      $sort: UserSortInput
    ) {
      users(filter: $filter, page: $page, size: $size, sort: $sort) {
        id
        displayName
        email
        photoURL
        role
      }
    }
  `

  const variables = {
    page: 0,
    size: 10,
    filter: { roles: ["ADMIN", "AGENT", "LEADER"] },
    sort: { displayName: "ASC" },
  }

  const { data, loading, fetchMore } = useQuery(USERS, {
    variables: {
      page: 0,
      size: 30,
      filter: { roles: ["ADMIN", "AGENT", "LEADER"] },
      sort: { displayName: "ASC" },
    },
    notifyOnNetworkStatusChange: true,
  })

  const users = data?.users?.map((user) => ({
    key: user.id,
    label: user.displayName,
    value: { ...user },
  }))

  const handleSearch = (_search, e) => {
    if (e.action === "input-change") {
      const search = _search?.toLowerCase()
      fetchMore({
        variables: { ...variables, filter: { ...variables.filter, search } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            users: uniqBy(
              [...prev.users, ...fetchMoreResult.users],
              "displayName",
            ),
          })
        },
      })
    }
  }

  return (
    <div className="w-full mt-16">
      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="account"
          label="Account (Payment) *"
          component="select"
          options={[
            { label: "Alpine Adventures", value: "ALPINE_ADVENTURES" },
            { label: "Adventures Africa", value: "ADVENTURES_AFRICA" },
          ]}
          placeholder="Select trip account"
        />
      </div>

      <div className="px-2 py-3 mt-2">
        <FieldAgency />
      </div>

      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="agents"
          label="Agents *"
          component="select"
          options={users?.filter((_) =>
            ["ADMIN", "AGENT"].includes(_?.value?.role),
          )}
          cacheOptions
          isMulti
          isLoading={loading}
          onInputChange={handleSearch}
          getOptionValue={(opt) => opt.key}
          mapping={{ label: "displayName" }}
          placeholder="Select trip agents"
        />
      </div>

      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="leaders"
          label="Leaders (optional)"
          component="select"
          options={users}
          cacheOptions
          isMulti
          isLoading={loading}
          onInputChange={handleSearch}
          getOptionValue={(opt) => opt?.key}
          mapping={{ label: "displayName" }}
          placeholder="Select trip leaders"
        />
      </div>
    </div>
  )
}
